import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import SignatureFieldPlacer from "./SignatureFieldPlacer";
import {
  Upload, FileText, FileSearch, Sparkles, PenTool,
  Send, Trash2, CheckCircle, Clock, AlertTriangle,
  X, ChevronRight, Download, Eye, RefreshCw, Mail,
  Shield, Zap, Brain, FileSignature, Plus, Inbox,
  FolderOpen, MousePointer, Save, ArrowLeft, Link2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const StatusBadge = ({ status }) => {
  const configs = {
    uploaded:    { label: "Uploaded",  color: "text-blue-400 bg-blue-500/10 border-blue-500/20" },
    analyzed:    { label: "Analyzed",  color: "text-purple-400 bg-purple-500/10 border-purple-500/20" },
    signed:      { label: "Signed",    color: "text-[#00FF88] bg-emerald-500/10 border-emerald-500/20" },
    sent:        { label: "Sent",      color: "text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/20" },
  };
  const c = configs[status] || configs.uploaded;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold border rounded ${c.color}`}>
      {c.label}
    </span>
  );
};

// ── Signature Pad ─────────────────────────────────────────────────────────────
function SignaturePad({ onSave, onCancel, defaultName = "" }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signerName, setSignerName] = useState(defaultName);
  const [lastPos, setLastPos] = useState(null);
  const [hasDrawn, setHasDrawn] = useState(false);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches?.[0] || e;
    return {
      x: (touch.clientX - rect.left) * (canvas.width / rect.width),
      y: (touch.clientY - rect.top) * (canvas.height / rect.height)
    };
  };

  const startDraw = useCallback((e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    setIsDrawing(true);
    setLastPos(getPos(e, canvas));
  }, []);

  const draw = useCallback((e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e, canvas);

    ctx.beginPath();
    ctx.moveTo(lastPos.x, lastPos.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = "#D4AF37";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();

    setLastPos(pos);
    setHasDrawn(true);
  }, [isDrawing, lastPos]);

  const stopDraw = useCallback(() => setIsDrawing(false), []);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const handleSave = () => {
    if (!signerName.trim()) { toast.error("Please enter your name to sign"); return; }
    if (!hasDrawn) { toast.error("Please draw your signature"); return; }
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL("image/png");
    onSave(signerName.trim(), dataUrl.split(",")[1]);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-[#0F0F0F] border border-[#D4AF37]/30 rounded-xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-[#D4AF37]" />
            <h3 className="text-white font-semibold">E-Signature</h3>
          </div>
          <button onClick={onCancel} className="text-white/40 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-4">
          <Label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Full Legal Name</Label>
          <Input
            value={signerName}
            onChange={(e) => setSignerName(e.target.value)}
            placeholder="Type your full name"
            className="bg-white/[0.03] border-white/[0.08] text-white h-9"
            data-testid="sig-name-input"
          />
        </div>

        <div className="mb-2">
          <Label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Draw Your Signature</Label>
          <div className="relative border border-white/[0.08] rounded bg-white/[0.02]">
            <canvas
              ref={canvasRef}
              width={400}
              height={120}
              className="w-full touch-none cursor-crosshair"
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={stopDraw}
              onMouseLeave={stopDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={stopDraw}
              data-testid="signature-canvas"
            />
            {!hasDrawn && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <p className="text-white/20 text-sm">Sign here</p>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mb-5">
          <button onClick={clearCanvas} className="text-xs text-white/40 hover:text-white flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Clear
          </button>
        </div>

        <div className="text-[10px] text-white/30 mb-4 flex items-center gap-1.5">
          <Shield className="w-3 h-3 text-[#00FF88]" />
          By signing, you agree this constitutes a legally binding e-signature.
        </div>

        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 py-2.5 border border-white/[0.08] text-white/50 hover:text-white text-sm rounded transition-all">
            Cancel
          </button>
          <Button onClick={handleSave} className="flex-1 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold" data-testid="sign-save-btn">
            <CheckCircle className="w-4 h-4 mr-1.5" /> Apply Signature
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Send Modal ────────────────────────────────────────────────────────────────
function SendModal({ doc, onSend, onCancel }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!email) { toast.error("Enter recipient email"); return; }
    setSending(true);
    try {
      await onSend(doc.doc_id, { recipient_email: email, recipient_name: name, message });
      toast.success(`Document sent to ${email}`);
      onCancel();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send document");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-[#0F0F0F] border border-white/[0.1] rounded-xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#D4AF37]" />
            <h3 className="text-white font-semibold">Send Document</h3>
          </div>
          <button onClick={onCancel} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <p className="text-white/50 text-xs mb-5 truncate">
          Sending: <span className="text-white">{doc.filename}</span>
        </p>

        <div className="space-y-4">
          <div>
            <Label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Recipient Email *</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@example.com" type="email" className="bg-white/[0.03] border-white/[0.08] text-white h-9" data-testid="send-email-input" />
          </div>
          <div>
            <Label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Recipient Name (optional)</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="John Smith" className="bg-white/[0.03] border-white/[0.08] text-white h-9" />
          </div>
          <div>
            <Label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Message (optional)</Label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Add a message for the recipient..." rows={3} className="w-full bg-white/[0.03] border border-white/[0.08] rounded px-3 py-2 text-sm text-white resize-none focus:outline-none focus:border-[#D4AF37]/50" />
          </div>
        </div>

        <div className="flex gap-3 mt-5">
          <button onClick={onCancel} className="flex-1 py-2.5 border border-white/[0.08] text-white/50 hover:text-white text-sm rounded transition-all">Cancel</button>
          <Button onClick={handleSend} disabled={sending} className="flex-1 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold" data-testid="send-doc-btn">
            {sending ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
            Send Document
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── AI Analysis Panel ─────────────────────────────────────────────────────────
function AnalysisPanel({ analysis, onClose }) {
  if (!analysis) return null;

  return (
    <div className="bg-[#0A0A0A] border border-purple-500/20 rounded-xl p-5 space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain className="w-4 h-4 text-purple-400" />
          <h4 className="text-white font-semibold text-sm">AI Document Analysis</h4>
        </div>
        <button onClick={onClose} className="text-white/30 hover:text-white"><X className="w-4 h-4" /></button>
      </div>

      {analysis.error ? (
        <p className="text-[#FF3B30] text-sm">{analysis.error}</p>
      ) : (
        <>
          {analysis.summary && (
            <div>
              <p className="text-[10px] text-purple-400 uppercase tracking-wider font-semibold mb-1.5">Summary</p>
              <p className="text-white/70 text-sm leading-relaxed">{analysis.summary}</p>
            </div>
          )}
          {analysis.document_type && (
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-white/30" />
              <span className="text-white/50 text-xs">Type:</span>
              <span className="text-white text-xs font-medium">{analysis.document_type}</span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            {analysis.key_terms?.length > 0 && (
              <div>
                <p className="text-[10px] text-[#D4AF37] uppercase tracking-wider font-semibold mb-2">Key Terms</p>
                <ul className="space-y-1">
                  {analysis.key_terms.slice(0, 6).map((term, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-xs text-white/60">
                      <span className="text-[#D4AF37] mt-0.5">›</span>{term}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {analysis.risk_flags?.length > 0 && (
              <div>
                <p className="text-[10px] text-[#FF3B30] uppercase tracking-wider font-semibold mb-2">Risk Flags</p>
                <ul className="space-y-1">
                  {analysis.risk_flags.slice(0, 4).map((flag, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-xs text-[#FF9F0A]">
                      <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />{flag}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {analysis.action_items?.length > 0 && (
            <div>
              <p className="text-[10px] text-[#00FF88] uppercase tracking-wider font-semibold mb-2">Action Items</p>
              <ul className="space-y-1">
                {analysis.action_items.slice(0, 5).map((item, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-white/60">
                    <CheckCircle className="w-3 h-3 text-[#00FF88] flex-shrink-0 mt-0.5" />{item}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {analysis.financial_summary && (
            <div className="bg-white/[0.02] border border-white/[0.06] rounded p-3">
              <p className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Financial</p>
              <p className="text-white/70 text-xs">{analysis.financial_summary}</p>
            </div>
          )}
          {analysis.critical_dates?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {analysis.critical_dates.slice(0, 4).map((date, i) => (
                <span key={i} className="text-[10px] px-2 py-1 bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#D4AF37] rounded">{date}</span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Signature Field Placer Modal ──────────────────────────────────────────────
function SignatureFieldModal({ doc, onSave, onCancel }) {
  const [fields, setFields] = useState(doc.signature_fields || []);
  const [saving, setSaving] = useState(false);
  const [rawText, setRawText] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const isPdf = doc.content_type === "application/pdf";

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/portal-docs/${doc.doc_id}/preview`, { withCredentials: true });
        setRawText(res.data.raw_text || "");
        setTotalPages(res.data.page_count || 1);
      } catch {
        setRawText("");
      } finally {
        setLoadingPreview(false);
      }
    })();
  }, [doc.doc_id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(doc.doc_id, fields);
      toast.success(`${fields.length} signature field${fields.length !== 1 ? "s" : ""} saved`);
      onCancel();
    } catch (err) {
      toast.error("Failed to save signature fields");
    } finally {
      setSaving(false);
    }
  };

  // Build content: text fallback for non-PDF, PDF served directly via pdfUrl prop
  const pdfFileUrl = isPdf ? `${BACKEND_URL}/api/portal-docs/${doc.doc_id}/file` : null;
  const docContent = rawText
    ? `<pre style="font-family:inherit;white-space:pre-wrap;font-size:13px;padding:32px;line-height:1.7;">${rawText.slice(0, 12000)}</pre>`
    : `<div style="text-align:center;padding:60px 20px;color:#888;">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24" style="margin:0 auto 16px;display:block;"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/></svg>
        <p style="font-size:15px;font-weight:600;margin-bottom:8px;">${doc.filename}</p>
        <p style="font-size:13px;">Click a field type above, then click anywhere to place it</p>
      </div>`;

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 bg-[#0A0A0A] border-b border-white/[0.08]">
        <div className="flex items-center gap-3">
          <button onClick={onCancel} className="text-white/50 hover:text-white flex items-center gap-1.5 text-sm">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="w-px h-5 bg-white/[0.1]" />
          <div>
            <span className="text-white font-semibold text-sm">{doc.doc_label || doc.filename}</span>
            <p className="text-white/40 text-xs">Place draggable signature fields — click a type, then click the document</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-white/40 text-sm">{fields.length} field{fields.length !== 1 ? "s" : ""} placed</span>
          <Button onClick={handleSave} disabled={saving} className="bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold" data-testid="save-sig-fields-btn">
            {saving ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            Save Fields
          </Button>
        </div>
      </div>

      {/* Placer */}
      <div className="flex-1 overflow-hidden">
        {loadingPreview ? (
          <div className="flex items-center justify-center h-full">
            <RefreshCw className="w-6 h-6 text-[#D4AF37] animate-spin" />
          </div>
        ) : (
          <SignatureFieldPlacer
            contractContent={isPdf ? null : docContent}
            pdfUrl={pdfFileUrl}
            totalPages={totalPages}
            onFieldsChange={setFields}
            initialFields={fields}
          />
        )}
      </div>
    </div>
  );
}

// ── Document Row (shared) ─────────────────────────────────────────────────────
function DocRow({ doc, isSelected, onClick }) {
  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
        isSelected
          ? "bg-[#D4AF37]/[0.06] border-[#D4AF37]/30"
          : "bg-white/[0.02] border-white/[0.06] hover:border-white/20"
      }`}
      data-testid={`doc-row-${doc.doc_id}`}
    >
      <div className="w-8 h-8 bg-white/[0.04] rounded flex items-center justify-center flex-shrink-0">
        <FileText className="w-4 h-4 text-white/40" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-xs font-medium truncate">{doc.doc_label || doc.filename}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <StatusBadge status={doc.status} />
          <span className="text-[10px] text-white/30">{formatFileSize(doc.file_size)}</span>
        </div>
      </div>
      <ChevronRight className={`w-4 h-4 transition-transform ${isSelected ? "rotate-90 text-[#D4AF37]" : "text-white/20"}`} />
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function PortalDocumentHub({ portalType = "partner" }) {
  const [activeTab, setActiveTab] = useState("my_docs");
  const [documents, setDocuments] = useState([]);
  const [inboxDocs, setInboxDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showSignPad, setShowSignPad] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showFieldPlacer, setShowFieldPlacer] = useState(false);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [label, setLabel] = useState("");
  const fileInputRef = useRef(null);

  const fetchDocuments = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/portal-docs/list`, { withCredentials: true });
      setDocuments(res.data.documents || []);
    } catch (err) {
      if (err.response?.status !== 401) toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const fetchInbox = async () => {
    setInboxLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/portal-docs/inbox`, { withCredentials: true });
      setInboxDocs(res.data.documents || []);
    } catch (err) {
      if (err.response?.status !== 401) toast.error("Failed to load inbox");
    } finally {
      setInboxLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  useEffect(() => {
    if (activeTab === "inbox") fetchInbox();
  }, [activeTab]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("doc_label", label || file.name);
      const res = await axios.post(`${BACKEND_URL}/api/portal-docs/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Document uploaded successfully");
      setDocuments(prev => [res.data, ...prev]);
      setLabel("");
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  };

  const handleAnalyze = async (docId) => {
    setAnalyzingId(docId);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/portal-docs/${docId}/analyze`, {}, { withCredentials: true });
      toast.success("AI analysis complete");
      setDocuments(prev => prev.map(d => d.doc_id === docId ? { ...d, status: "analyzed", ai_analysis: res.data.analysis } : d));
      if (selectedDoc?.doc_id === docId) setSelectedDoc(prev => ({ ...prev, status: "analyzed", ai_analysis: res.data.analysis }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analysis failed");
    } finally {
      setAnalyzingId(null);
    }
  };

  const handleSign = async (signerName, signatureData) => {
    if (!selectedDoc) return;
    try {
      await axios.post(`${BACKEND_URL}/api/portal-docs/${selectedDoc.doc_id}/sign`, { signer_name: signerName, signature_data: signatureData }, { withCredentials: true });
      toast.success("Document signed successfully");
      setDocuments(prev => prev.map(d => d.doc_id === selectedDoc.doc_id ? { ...d, status: "signed", signature: { signer_name: signerName } } : d));
      setSelectedDoc(prev => ({ ...prev, status: "signed" }));
      setShowSignPad(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signing failed");
    }
  };

  const handleInboxSign = async (signerName, signatureData) => {
    if (!selectedDoc) return;
    try {
      await axios.post(`${BACKEND_URL}/api/portal-docs/${selectedDoc.doc_id}/inbox-sign`, { signer_name: signerName, signature_data: signatureData }, { withCredentials: true });
      toast.success("Document signed from inbox");
      setInboxDocs(prev => prev.map(d => d.doc_id === selectedDoc.doc_id ? { ...d, status: "signed" } : d));
      setSelectedDoc(prev => ({ ...prev, status: "signed" }));
      setShowSignPad(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Inbox signing failed");
    }
  };

  const handleSend = async (docId, payload) => {
    await axios.post(`${BACKEND_URL}/api/portal-docs/${docId}/send`, payload, { withCredentials: true });
    fetchDocuments();
  };

  const handleShareLink = async (docId) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/portal-docs/${docId}/share-link`, {}, { withCredentials: true });
      const shareUrl = `${window.location.origin}/sign/doc/${res.data.share_token}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Shareable signing link copied to clipboard!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate share link");
    }
  };

  const handleSaveFields = async (docId, fields) => {
    await axios.post(`${BACKEND_URL}/api/portal-docs/${docId}/signature-fields`, { fields }, { withCredentials: true });
    setDocuments(prev => prev.map(d => d.doc_id === docId ? { ...d, signature_fields: fields } : d));
    if (selectedDoc?.doc_id === docId) setSelectedDoc(prev => ({ ...prev, signature_fields: fields }));
  };

  const handleDelete = async (docId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/portal-docs/${docId}`, { withCredentials: true });
      toast.success("Document deleted");
      setDocuments(prev => prev.filter(d => d.doc_id !== docId));
      if (selectedDoc?.doc_id === docId) setSelectedDoc(null);
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  // ── Signature Field Placer full-screen overlay ──────────────────────────────
  if (showFieldPlacer && selectedDoc) {
    return (
      <SignatureFieldModal
        doc={selectedDoc}
        onSave={handleSaveFields}
        onCancel={() => setShowFieldPlacer(false)}
      />
    );
  }

  const currentDocs = activeTab === "inbox" ? inboxDocs : documents;
  const isInbox = activeTab === "inbox";

  return (
    <div className="space-y-6" data-testid="portal-doc-hub">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Document Intelligence</h2>
          <p className="text-white/40 text-sm mt-0.5">Upload, analyze with AI, place signature fields, e-sign and send deal documents</p>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1 bg-purple-500/10 border border-purple-500/20 rounded">
          <Brain className="w-3 h-3 text-purple-400" />
          <span className="text-[10px] text-purple-400 uppercase tracking-wider font-semibold">AI Powered</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-white/[0.03] border border-white/[0.06] rounded-lg w-fit">
        <button
          onClick={() => { setActiveTab("my_docs"); setSelectedDoc(null); }}
          className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-all ${
            activeTab === "my_docs"
              ? "bg-[#D4AF37] text-black"
              : "text-white/50 hover:text-white"
          }`}
          data-testid="tab-my-docs"
        >
          <FolderOpen className="w-4 h-4" />
          My Documents
          {documents.length > 0 && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${activeTab === "my_docs" ? "bg-black/20 text-black" : "bg-white/10 text-white/60"}`}>
              {documents.length}
            </span>
          )}
        </button>
        <button
          onClick={() => { setActiveTab("inbox"); setSelectedDoc(null); }}
          className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-all ${
            activeTab === "inbox"
              ? "bg-[#D4AF37] text-black"
              : "text-white/50 hover:text-white"
          }`}
          data-testid="tab-inbox"
        >
          <Inbox className="w-4 h-4" />
          Inbox
          {inboxDocs.length > 0 && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${activeTab === "inbox" ? "bg-black/20 text-black" : "bg-white/10 text-white/60"}`}>
              {inboxDocs.length}
            </span>
          )}
        </button>
      </div>

      <div className="grid grid-cols-5 gap-6">
        {/* Left: Upload (my docs only) + List */}
        <div className="col-span-2 space-y-4">
          {!isInbox && (
            <>
              <div
                className={`border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
                  dragOver ? "border-[#D4AF37]/60 bg-[#D4AF37]/[0.06]" : "border-white/[0.08] bg-white/[0.01] hover:border-white/20"
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                data-testid="doc-upload-area"
              >
                <input ref={fileInputRef} type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.txt,.doc,.docx" onChange={handleFileSelect} />
                <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                  {uploading ? <RefreshCw className="w-6 h-6 text-[#D4AF37] animate-spin" /> : <Upload className="w-6 h-6 text-[#D4AF37]" />}
                </div>
                <p className="text-white text-sm font-medium mb-1">{uploading ? "Uploading..." : "Drop document here"}</p>
                <p className="text-white/30 text-xs">PDF, PNG, JPG, TXT • Max 15MB</p>
              </div>

              <div>
                <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Optional label (e.g. Purchase Agreement)" className="bg-white/[0.02] border-white/[0.06] text-white text-xs h-8" />
              </div>
            </>
          )}

          {isInbox && (
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-500/[0.06] border border-blue-500/20 rounded-lg">
              <Inbox className="w-4 h-4 text-blue-400" />
              <p className="text-blue-400 text-xs">Documents sent to your account email appear here. You can view, sign, and download them.</p>
            </div>
          )}

          {/* Document List */}
          <div className="space-y-2">
            <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold">
              {isInbox ? "Received" : "Documents"} ({currentDocs.length})
            </p>
            {(isInbox ? inboxLoading : loading) ? (
              <div className="text-center py-8">
                <RefreshCw className="w-6 h-6 text-white/20 animate-spin mx-auto" />
              </div>
            ) : currentDocs.length === 0 ? (
              <div className="text-center py-10 bg-white/[0.01] border border-white/[0.04] rounded-xl">
                {isInbox ? <Inbox className="w-10 h-10 text-white/10 mx-auto mb-2" /> : <FileText className="w-10 h-10 text-white/10 mx-auto mb-2" />}
                <p className="text-white/30 text-sm">{isInbox ? "No received documents" : "No documents yet"}</p>
                <p className="text-white/20 text-xs mt-1">{isInbox ? "Documents sent to you will appear here" : "Upload your first document above"}</p>
              </div>
            ) : (
              currentDocs.map((doc) => (
                <DocRow
                  key={doc.doc_id}
                  doc={doc}
                  isSelected={selectedDoc?.doc_id === doc.doc_id}
                  onClick={() => setSelectedDoc(selectedDoc?.doc_id === doc.doc_id ? null : doc)}
                />
              ))
            )}
          </div>
        </div>

        {/* Right: Document Actions Panel */}
        <div className="col-span-3 space-y-4">
          {selectedDoc ? (
            <>
              {/* Doc Header */}
              <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold truncate">{selectedDoc.doc_label || selectedDoc.filename}</h3>
                    <p className="text-white/40 text-xs mt-0.5 truncate">{selectedDoc.filename}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <StatusBadge status={selectedDoc.status} />
                      <span className="text-[10px] text-white/30">{formatFileSize(selectedDoc.file_size)}</span>
                      <span className="text-[10px] text-white/30">{new Date(selectedDoc.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  {!isInbox && (
                    <button onClick={() => handleDelete(selectedDoc.doc_id)} className="text-white/20 hover:text-[#FF3B30] p-1 ml-4 flex-shrink-0 transition-colors" data-testid="delete-doc-btn">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Signature info */}
                {selectedDoc.signature && (
                  <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-emerald-500/[0.06] border border-emerald-500/20 rounded-lg">
                    <CheckCircle className="w-3.5 h-3.5 text-[#00FF88]" />
                    <span className="text-xs text-[#00FF88]">Signed by {selectedDoc.signature.signer_name}</span>
                  </div>
                )}

                {/* Sent info */}
                {selectedDoc.sent_to?.length > 0 && (
                  <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-[#D4AF37]/[0.06] border border-[#D4AF37]/20 rounded-lg">
                    <Send className="w-3.5 h-3.5 text-[#D4AF37]" />
                    <span className="text-xs text-[#D4AF37]">
                      Sent to {selectedDoc.sent_to.length} recipient{selectedDoc.sent_to.length > 1 ? "s" : ""}
                    </span>
                  </div>
                )}

                {/* Signature fields badge */}
                {selectedDoc.signature_fields?.length > 0 && (
                  <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-blue-500/[0.06] border border-blue-500/20 rounded-lg">
                    <MousePointer className="w-3.5 h-3.5 text-blue-400" />
                    <span className="text-xs text-blue-400">
                      {selectedDoc.signature_fields.length} signature field{selectedDoc.signature_fields.length !== 1 ? "s" : ""} placed
                    </span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              {!isInbox ? (
                <div className="grid grid-cols-5 gap-2">
                  <button
                    onClick={() => handleAnalyze(selectedDoc.doc_id)}
                    disabled={analyzingId === selectedDoc.doc_id}
                    className="flex flex-col items-center gap-2 p-3 bg-purple-500/[0.06] border border-purple-500/20 hover:bg-purple-500/[0.12] rounded-xl transition-all disabled:opacity-50"
                    data-testid="analyze-doc-btn"
                  >
                    {analyzingId === selectedDoc.doc_id ? <RefreshCw className="w-4 h-4 text-purple-400 animate-spin" /> : <Brain className="w-4 h-4 text-purple-400" />}
                    <span className="text-[10px] text-purple-400 font-medium">{analyzingId === selectedDoc.doc_id ? "Analyzing" : "AI Analyze"}</span>
                  </button>

                  <button
                    onClick={() => setShowFieldPlacer(true)}
                    className="flex flex-col items-center gap-2 p-3 bg-blue-500/[0.06] border border-blue-500/20 hover:bg-blue-500/[0.12] rounded-xl transition-all"
                    data-testid="place-fields-btn"
                  >
                    <MousePointer className="w-4 h-4 text-blue-400" />
                    <span className="text-[10px] text-blue-400 font-medium">Place Fields</span>
                  </button>

                  <button
                    onClick={() => setShowSignPad(true)}
                    className="flex flex-col items-center gap-2 p-3 bg-[#D4AF37]/[0.06] border border-[#D4AF37]/20 hover:bg-[#D4AF37]/[0.12] rounded-xl transition-all"
                    data-testid="sign-doc-btn"
                  >
                    <PenTool className="w-4 h-4 text-[#D4AF37]" />
                    <span className="text-[10px] text-[#D4AF37] font-medium">{selectedDoc.signature ? "Re-Sign" : "E-Sign"}</span>
                  </button>

                  <button
                    onClick={() => handleShareLink(selectedDoc.doc_id)}
                    className="flex flex-col items-center gap-2 p-3 bg-indigo-500/[0.06] border border-indigo-500/20 hover:bg-indigo-500/[0.12] rounded-xl transition-all"
                    data-testid="share-link-btn"
                  >
                    <Link2 className="w-4 h-4 text-indigo-400" />
                    <span className="text-[10px] text-indigo-400 font-medium">Share Link</span>
                  </button>

                  <button
                    onClick={() => setShowSendModal(true)}
                    className="flex flex-col items-center gap-2 p-3 bg-emerald-500/[0.06] border border-emerald-500/20 hover:bg-emerald-500/[0.12] rounded-xl transition-all"
                    data-testid="send-doc-open-btn"
                  >
                    <Send className="w-4 h-4 text-[#00FF88]" />
                    <span className="text-[10px] text-[#00FF88] font-medium">Send</span>
                  </button>
                </div>
              ) : (
                /* Inbox actions: only sign */
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setShowSignPad(true)}
                    className="flex flex-col items-center gap-2 p-4 bg-[#D4AF37]/[0.06] border border-[#D4AF37]/20 hover:bg-[#D4AF37]/[0.12] rounded-xl transition-all"
                    data-testid="inbox-sign-btn"
                  >
                    <PenTool className="w-5 h-5 text-[#D4AF37]" />
                    <span className="text-xs text-[#D4AF37] font-medium">Sign Document</span>
                  </button>

                  <div className="flex flex-col items-center gap-2 p-4 bg-white/[0.02] border border-white/[0.06] rounded-xl opacity-50">
                    <Download className="w-5 h-5 text-white/40" />
                    <span className="text-xs text-white/40 font-medium">Download (soon)</span>
                  </div>
                </div>
              )}

              {/* AI Analysis Panel */}
              {selectedDoc.ai_analysis && (
                <AnalysisPanel
                  analysis={selectedDoc.ai_analysis}
                  onClose={() => setSelectedDoc(prev => ({ ...prev, ai_analysis: null }))}
                />
              )}

              {/* Inbox: Show signature fields if any */}
              {isInbox && selectedDoc.signature_fields?.length > 0 && (
                <div className="bg-[#0A0A0A] border border-blue-500/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <MousePointer className="w-4 h-4 text-blue-400" />
                    <h4 className="text-white text-sm font-semibold">Signature Fields Required</h4>
                  </div>
                  <div className="space-y-2">
                    {selectedDoc.signature_fields.map((f, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-white/60">
                        <span className="px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded">{f.type}</span>
                        <span className="capitalize">{f.role}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-24 bg-white/[0.01] border border-white/[0.04] rounded-xl">
              <div className="w-16 h-16 bg-[#D4AF37]/[0.06] rounded-2xl flex items-center justify-center mb-4">
                <FileSearch className="w-8 h-8 text-[#D4AF37]/50" />
              </div>
              <p className="text-white/30 text-sm">
                {isInbox ? "Select a received document" : "Select a document to manage"}
              </p>
              <p className="text-white/20 text-xs mt-1">
                {isInbox ? "Review and sign documents sent to you" : "AI analyze, place fields, e-sign, or send"}
              </p>

              <div className="flex flex-wrap gap-2 mt-6 justify-center">
                {!isInbox ? [
                  { icon: Brain, label: "AI Analysis", color: "text-purple-400 border-purple-500/20" },
                  { icon: MousePointer, label: "Place Fields", color: "text-blue-400 border-blue-500/20" },
                  { icon: PenTool, label: "E-Signature", color: "text-[#D4AF37] border-[#D4AF37]/20" },
                  { icon: Send, label: "Email Send", color: "text-[#00FF88] border-emerald-500/20" },
                ].map(({ icon: Icon, label, color }) => (
                  <div key={label} className={`flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.02] border rounded-full ${color}`}>
                    <Icon className="w-3 h-3" />
                    <span className="text-[10px] font-medium">{label}</span>
                  </div>
                )) : [
                  { icon: Inbox, label: "Received Docs", color: "text-blue-400 border-blue-500/20" },
                  { icon: PenTool, label: "Quick Sign", color: "text-[#D4AF37] border-[#D4AF37]/20" },
                  { icon: Shield, label: "Audit Trail", color: "text-[#00FF88] border-emerald-500/20" },
                ].map(({ icon: Icon, label, color }) => (
                  <div key={label} className={`flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.02] border rounded-full ${color}`}>
                    <Icon className="w-3 h-3" />
                    <span className="text-[10px] font-medium">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showSignPad && selectedDoc && (
        <SignaturePad
          onSave={isInbox ? handleInboxSign : handleSign}
          onCancel={() => setShowSignPad(false)}
          defaultName=""
        />
      )}

      {showSendModal && selectedDoc && (
        <SendModal
          doc={selectedDoc}
          onSend={handleSend}
          onCancel={() => setShowSendModal(false)}
        />
      )}
    </div>
  );
}
